import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@crashx/ui";
import { useChatAccess } from "./useChatAccess.js";
import { useChatStore } from "./chatStore.js";
import { ChatComposer } from "./ChatComposer.js";
import { ChatHistory } from "./ChatHistory.js";
import { RainForm } from "./RainForm.js";
import { ChatAuthForm } from "./ChatAuthForm.js";
import { getConnection } from "../../net/connection.js";

export function ChatPanel({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => window.matchMedia("(min-width: 1100px)").matches);
  const trigger = useRef<HTMLButtonElement>(null);
  const { access, config, error, connected } = useChatAccess();
  const restriction = useChatStore((state) => state.restriction);
  const epoch = useChatStore((state) => state.epoch);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const expiry = [restriction.mutedUntil, restriction.bannedUntil].filter((value) => value > Date.now());
    if (!expiry.length) return;
    const timer = setTimeout(
      () => {
        setNow(Date.now());
        if (restriction.bannedUntil > 0 && restriction.bannedUntil <= Date.now()) {
          getConnection().disconnect();
          getConnection().connect();
        }
      },
      Math.min(...expiry) - Date.now() + 50,
    );
    return () => clearTimeout(timer);
  }, [restriction, now]);
  const banned = restriction.bannedUntil > Math.max(now, Date.now());
  const muted = restriction.mutedUntil > Math.max(now, Date.now());
  const canSend = access?.permissions.includes("chat.send") ?? false;
  const disabled = !connected || muted || banned;
  const expanded = mobileOpen || open;
  return (
    <aside
      className="cx-chat-panel"
      aria-label={t("chat.title")}
      onKeyDown={(event) => {
        if (event.key === "Escape" && expanded) {
          setOpen(false);
          onMobileClose?.();
          trigger.current?.focus();
        }
      }}
    >
      <Button
        ref={trigger}
        variant="ghost"
        aria-expanded={expanded}
        aria-controls="chat-panel-body"
        onClick={() => {
          if (mobileOpen) onMobileClose?.();
          else setOpen(!open);
        }}
      >
        {t(expanded ? "chat.close" : "chat.open")}
      </Button>
      <div id="chat-panel-body" hidden={!expanded} className="cx-chat-body">
        <h2 className="m-0 text-base">{t("chat.title")}</h2>
        {error ? (
          <p role="alert" className="text-danger">
            {t(error)}
          </p>
        ) : null}
        {!config ? (
          <p role="status">{t("app.loading")}</p>
        ) : !config.enabled ? (
          <p>{t("errors.CHAT_DISABLED")}</p>
        ) : (
          <>
            {banned ? (
              <p role="status">{t("chat.banned")}</p>
            ) : (
              <ChatHistory
                canModerate={connected && (access?.permissions.includes("chat.moderate") ?? false)}
              />
            )}
            {muted ? <p role="status">{t("errors.CHAT_MUTED")}</p> : null}
            {canSend ? (
              <div key={epoch} className="flex flex-col gap-3">
                <ChatComposer disabled={disabled} maxLength={config.maxLength} />
                {access?.permissions.includes("chat.rain") ? (
                  <RainForm
                    disabled={disabled}
                    maxAmount={config.rainMaxAmount}
                    maxRecipients={config.rainMaxRecipients}
                  />
                ) : null}
              </div>
            ) : access?.guest || error === "errors.UNAUTHENTICATED" ? (
              <>
                <p>{t("chat.signInHint")}</p>
                <ChatAuthForm />
              </>
            ) : null}
          </>
        )}
      </div>
    </aside>
  );
}
